import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Button,
    Input,
    useMultiStyleConfig,
    Text,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useProductRegistrationFields from './useProductRegistrationFields'
import {FormattedMessage} from 'react-intl'
import Field from '../../../../components/field'
import {CalendarIcon, CheckIcon} from '../../../../components/icons'
import {compressImageForUpload, validateFile} from '../../../../utils/upload-utils'
import LoadingSpinner from '../../../../components/loading-spinner'
import ReceiptValidationAlert from '../receipt-validation-alert'
import {BRAND_KEY} from '../../../../brand'
import {useCustomPreferenceWithLocale} from '../../../../hooks/use-custom-preference'

const ProductRegistrationFields = ({
    form,
    prefix = '',
    setReceiptBase64,
    setReceiptName,
    setReceiptMime,
    setReceiptFile,
    initialFile,
    disableSerialField,
    allowSerialEdit = false,
    isEditForm,
    isPricePaidLocked = true,
    fileError,
    setFileError, // Accept setFileError as a prop
    isGuest,
    isAccount = false,
    clearCalendarIcon = false,
    onReceiptFileReady,
    isReceiptScanning = false,
    receiptValidated = false,
    receiptTaggunError = '',
    allowSourceEdit = false,
    showSerialField = true,
    showManualFields = false,
    onShowManualFields,
    receiptScanResult,
    onRetry,
    isSNVariant = false,
    isSerialNumberRequired = false,
}) => {
    const fields = useProductRegistrationFields({form, prefix, isSerialNumberRequired})
    const formRef = useRef()
    const fileInputRef = useRef()
    const formStyles = useMultiStyleConfig('ProductRegistrationForm')
    // create three formatted messages for the file name, error message and the upload file button
    const uploadFileLabel = (
        <FormattedMessage
            id="product_registration.upload_file"
            defaultMessage="Upload file (Max 5 MB)*"
        />
    )
    const uploadFileError = (
        <FormattedMessage
            id="product_registration.upload_file_error"
            defaultMessage="Please upload a file."
        />
    )
    const uploadFileSizeError = (
        <FormattedMessage
            id="product_registration.upload_file_size_error"
            defaultMessage="File too large. The maximum allowed size is 5MB."
        />
    )
    const uploadFileValidityError = (
        <FormattedMessage
            id="product_registration.upload_file_type_error"
            defaultMessage="Only PDF/JPEG/PNG files are allowed"
        />
    )
    const [fileName, setFileName] = useState(uploadFileLabel)
    const serialNumberHelpLink = useCustomPreferenceWithLocale('serialNumberHelpLink')
    const isMissingRetailer = receiptTaggunError === 'missing_retailer'
    const shouldShowFields =
        (receiptValidated && !receiptTaggunError) || isMissingRetailer || showManualFields
    const shouldLockFields = receiptValidated || isMissingRetailer
    const shouldLockPricePaid = isEditForm && isPricePaidLocked

    useEffect(() => {
        // Focus on the form when the component mounts for accessibility
        formRef?.current?.focus()
    }, [])

    useEffect(() => {
        if (initialFile) {
            setFileName(initialFile.name)
            setReceiptName(initialFile.name)
            setReceiptMime(initialFile.type)
        }
    }, [initialFile])

    const handleFileChange = async (e) => {
        const file = e.target.files[0]
        if (!file) {
            setFileError(uploadFileError)
            return
        }

        if (file.size > 5 * 1024 * 1024) {
            setFileError(uploadFileSizeError)
            return
        }

        const isValid = await validateFile(file)

        if (!isValid) {
            setFileError(uploadFileValidityError)
            return
        }

        setFileError('') // Clear fileError in parent
        setReceiptName(file.name)
        setReceiptMime(file.type)
        setFileName(file.name)
        setReceiptFile(file) // Pass the file object itself

        // Compress image files client-side to avoid exceeding the AWS Lambda-like
        // payload limit (~6 MB) on Salesforce MRT deployed environments.
        const fileToUpload = await compressImageForUpload(file)

        // Create a FileReader to convert file to base64
        const reader = new FileReader()
        reader.onloadend = () => {
            // The result is a Data URL: "data:<mime type>;base64,<encoded data>"
            // Split at the comma to remove the "data:" prefix
            const base64String = reader.result.split(',')[1]
            setReceiptBase64(base64String)
        }

        // Read the entire file as a Data URL
        reader.readAsDataURL(fileToUpload)

        // Send original file to Taggun for receipt validation
        if (onReceiptFileReady) {
            onReceiptFileReady(file)
        }
    }

    const onRetryButton = () => {
        onRetry()
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const onUploadButtonClick = () => {
        if (onRetry) {
            onRetry()
        }

        // When users upload again, clear previously extracted/manual values.
        form.setValue(fields.purchasedate.name, '')
        form.setValue(fields.source.name, '')
        form.setValue(fields.pricePaid.name, '')
        setReceiptBase64('')
        setReceiptName('')
        setReceiptMime('')
        setReceiptFile(null)
        setFileError('')
        setFileName(uploadFileLabel)

        if (fileInputRef.current) {
            fileInputRef.current.value = ''
            fileInputRef.current.click()
        }
    }

    return (
        <Flex {...formStyles.formInnerContainer}>
            {/* File upload section */}
            <Flex {...formStyles.uploadFileContainer} align="flex-start" gap={2}>
                <Button
                    {...formStyles.fileButton}
                    variant={'primary'}
                    isDisabled={isGuest || isReceiptScanning}
                    data-testid="upload-file-button"
                    aria-describedby={'upload-file-input-instruction'}
                    onClick={onUploadButtonClick}
                >
                    {fields.uploadFile.label}
                </Button>

                <Flex direction="column" flex={1}>
                    <Flex
                        {...formStyles.fileNameContainer}
                        borderColor={fileError ? 'red' : undefined}
                        aria-invalid={!!fileError}
                        aria-describedby={fileError ? 'upload-file-error' : undefined}
                        justifyContent={'space-between'}
                    >
                        <Text {...formStyles.fileNameLabel} id={'upload-file-input-instruction'}>
                            {fileName}
                        </Text>
                        {!isReceiptScanning &&
                            (receiptValidated || receiptTaggunError) &&
                            fileName !== uploadFileLabel && (
                                <CheckIcon w={'24px'} h={'24px'} color={'text.success'} />
                            )}
                    </Flex>

                    {fileError && (
                        <Text
                            id={'upload-file-error'}
                            {...formStyles.fileUploadErrorMessage}
                            role="alert"
                            aria-live="polite"
                            aria-atomic="true"
                        >
                            {fileError}
                        </Text>
                    )}
                </Flex>
                <Input
                    type="file"
                    ref={fileInputRef}
                    id={fields.uploadFile.name}
                    {...fields.uploadFile}
                    onChange={handleFileChange}
                    accept="image/jpeg,image/png,image/gif,image/heic,application/pdf"
                    style={{display: 'none'}}
                />
            </Flex>

            {/* Spinner while Taggun is scanning */}
            {isReceiptScanning && <LoadingSpinner />}

            {/* Taggun error box with scenario-specific messages */}
            {!isReceiptScanning && receiptTaggunError && (
                <ReceiptValidationAlert
                    result={receiptScanResult}
                    isTechnicalError={!receiptScanResult}
                    onShowManualFields={onShowManualFields}
                    onRetry={onRetryButton}
                />
            )}

            {/* Date and Source fields — shown when Taggun succeeded or user chose manual entry */}
            {shouldShowFields && (
                <>
                    <Field
                        {...fields.purchasedate}
                        type="date"
                        clearIcon={isAccount || clearCalendarIcon}
                        calendarIcon={<CalendarIcon />}
                        {...form.register(fields.purchasedate.name, fields.purchasedate.rules)}
                        isEditForm={isEditForm}
                        isDisabled={isAccount || shouldLockFields}
                        isFieldRequired={true}
                    />
                    <Field
                        {...fields.source}
                        isDisabled={
                            isGuest ||
                            (receiptValidated && !allowSourceEdit && !isMissingRetailer) ||
                            (shouldLockFields && !isMissingRetailer)
                        }
                        placeholder={
                            <FormattedMessage
                                id="product_registration.where_did_you_buy"
                                defaultMessage="Where did you make the purchase?"
                            />
                        }
                        isFieldRequired={true}
                        ariaLabel={{
                            id: 'product_registration.where_did_you_buy-aria-label',
                            defaultMessage: 'Select a store',
                        }}
                    />
                    <Flex direction="column" alignSelf="stretch" gap={1}>
                        <Field
                            {...fields.pricePaid}
                            {...form.register(fields.pricePaid.name, fields.pricePaid.rules)}
                            isDisabled={shouldLockPricePaid}
                            isFieldRequired={false}
                            ariaLabel={{
                                id: 'product_registration.price_paid-aria-label',
                                defaultMessage: 'Enter the price paid',
                            }}
                        />
                        {!shouldLockPricePaid ? (
                            <Text {...formStyles.linkText}>
                                <FormattedMessage
                                    id="product_registration.price_paid_receipt_note"
                                    defaultMessage="Please make sure that the value corresponds to the price paid for the product as shown on your receipt. This field is optional, but <u>please note that without this information you won't be able to participate in special promotions or offers that rely on this data.</u>"
                                    values={{u: (chunks) => <u>{chunks}</u>}}
                                />
                            </Text>
                        ) : (
                            <Text {...formStyles.linkText}>
                                <FormattedMessage
                                    id="product_registration.price_paid_receipt_modified"
                                    defaultMessage="The purchase price cannot be modified. Please contact Customer Care for assistance"
                                />
                            </Text>
                        )}
                    </Flex>
                    {showSerialField && (
                        <Flex {...formStyles.skuNumberContainer}>
                            <Field
                                {...fields.serialNumber}
                                isDisabled={
                                    disableSerialField ||
                                    isGuest ||
                                    (shouldLockFields && isSNVariant && !isSerialNumberRequired) ||
                                    (isEditForm && !allowSerialEdit)
                                }
                                isFieldRequired={isSerialNumberRequired}
                            />
                            {BRAND_KEY !== 'nutribullet' && serialNumberHelpLink && (
                                <Text {...formStyles.linkText}>
                                    <FormattedMessage
                                        id="product_registration_fields.serial_number_link"
                                        defaultMessage="<u>Where can I find the machine's serial number?</u>"
                                        values={{
                                            u: (chunks) => (
                                                <u>
                                                    <a
                                                        href={serialNumberHelpLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {chunks}
                                                    </a>
                                                </u>
                                            ),
                                        }}
                                    />
                                </Text>
                            )}
                        </Flex>
                    )}
                </>
            )}
        </Flex>
    )
}

ProductRegistrationFields.propTypes = {
    form: PropTypes.object.isRequired,
    prefix: PropTypes.string,
    disableSerialField: PropTypes.bool,
    isEditForm: PropTypes.bool,
    isPricePaidLocked: PropTypes.bool,
    formTitleAriaLabel: PropTypes.object,
    setReceiptBase64: PropTypes.func.isRequired,
    setReceiptName: PropTypes.func.isRequired,
    setReceiptMime: PropTypes.func.isRequired,
    setReceiptFile: PropTypes.func.isRequired,
    initialFile: PropTypes.object,
    fileError: PropTypes.string,
    setFileError: PropTypes.func.isRequired,
    allowSerialEdit: PropTypes.bool,
    onReceiptFileReady: PropTypes.func,
    isReceiptScanning: PropTypes.bool,
    receiptValidated: PropTypes.bool,
    receiptTaggunError: PropTypes.string,
    allowSourceEdit: PropTypes.bool,
    showSerialField: PropTypes.bool,
    showManualFields: PropTypes.bool,
    onShowManualFields: PropTypes.func,
    receiptScanResult: PropTypes.object,
    onRetry: PropTypes.func,
    isGuest: PropTypes.bool,
    isAccount: PropTypes.bool,
    clearCalendarIcon: PropTypes.bool,
    isSNVariant: PropTypes.bool,
    isSerialNumberRequired: PropTypes.bool,
}

export default ProductRegistrationFields
